import { LoadingOutlined } from '@ant-design/icons';
import { Boundary } from 'components/common';
import { PLAN_OPTIONS, getPlanLabel, getUserPlan } from 'constants/plans';
import { useDocumentTitle, useScrollTop } from 'hooks';
import PropType from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useState
} from 'react';
import { useSelector } from 'react-redux';
import firebase from 'services/firebase';

const USERS_PAGE_SIZE = 20;
const EMPTY_ALIASES = [];

const getRegistrationTime = (user) => {
  const value = user.dateJoined || user.createdAt;

  if (!value) return 0;
  if (typeof value === 'number') return value;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value.seconds) return value.seconds * 1000;

  const parsedTime = new Date(value).getTime();
  return Number.isNaN(parsedTime) ? 0 : parsedTime;
};

const UserCard = React.memo(({
  aliases,
  currentAdminId,
  isSaving,
  onActivate,
  onUpdate,
  user
}) => {
  const [activationId, setActivationId] = useState('');
  let activationButtonText = aliases.length ? 'Add Alias' : 'Activate';
  if (isSaving) activationButtonText = 'Saving...';

  const activate = async () => {
    const saved = await onActivate(user.id, activationId);
    if (saved) setActivationId('');
  };

  return (
    <article className="admin-user-card">
      <div className="admin-user-summary">
        <div>
          <h4>{user.fullname || 'Unnamed user'}</h4>
          <p>{user.email || 'No email'}</p>
        </div>
        <span className={aliases.length ? 'admin-user-active' : 'admin-user-pending'}>
          {aliases.length ? 'Activated' : 'Not activated'}
        </span>
      </div>

      <label htmlFor={`user-id-${user.id}`}>
        User document ID
        <input className="input-form" id={`user-id-${user.id}`} readOnly value={user.id} />
      </label>

      <div className="admin-user-controls">
        <label htmlFor={`user-plan-${user.id}`}>
          Plan
          <select
            className="input-form"
            disabled={isSaving}
            id={`user-plan-${user.id}`}
            onChange={(event) => onUpdate(user.id, { plan: event.target.value })}
            value={getUserPlan(user.plan)}
          >
            {PLAN_OPTIONS.map((plan) => (
              <option key={plan.value} value={plan.value}>{plan.label}</option>
            ))}
          </select>
        </label>

        <label htmlFor={`user-role-${user.id}`}>
          Role
          <select
            className="input-form"
            disabled={isSaving || currentAdminId === user.id}
            id={`user-role-${user.id}`}
            onChange={(event) => onUpdate(user.id, { role: event.target.value })}
            value={user.role || 'USER'}
          >
            <option value="USER">User</option>
            <option value="ADMIN">Admin</option>
          </select>
        </label>
      </div>

      <div className="admin-user-aliases">
        <strong>Profile links:</strong>
        {' '}
        {aliases.length ? aliases.map((alias, index) => (
          <React.Fragment key={alias}>
            {index > 0 ? ', ' : null}
            <a href={`/#/user/${alias}`} rel="noopener noreferrer" target="_blank">
              {alias}
            </a>
          </React.Fragment>
        )) : 'None'}
      </div>

      <div className="admin-user-activation">
        <input
          className="input-form"
          disabled={isSaving}
          onChange={(event) => setActivationId(event.target.value)}
          placeholder="Profile link ID, for example: temp"
          value={activationId}
        />
        <button
          className="button button-small"
          disabled={isSaving}
          onClick={activate}
          type="button"
        >
          {activationButtonText}
        </button>
      </div>
      <small className="text-subtle">
        Effective plan:
        {' '}
        {getPlanLabel(user.plan)}
      </small>
    </article>
  );
});

UserCard.propTypes = {
  aliases: PropType.arrayOf(PropType.string).isRequired,
  currentAdminId: PropType.string,
  isSaving: PropType.bool.isRequired,
  onActivate: PropType.func.isRequired,
  onUpdate: PropType.func.isRequired,
  user: PropType.shape({
    email: PropType.string,
    fullname: PropType.string,
    id: PropType.string.isRequired,
    plan: PropType.string,
    role: PropType.string
  }).isRequired
};

UserCard.defaultProps = {
  currentAdminId: ''
};

const Users = () => {
  useDocumentTitle('Users | TaKKa Admin');
  useScrollTop();

  const currentAdminId = useSelector((state) => state.auth?.id);
  const [users, setUsers] = useState([]);
  const [profileAliases, setProfileAliases] = useState({});
  const [isLoading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState('');
  const [message, setMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleCount, setVisibleCount] = useState(USERS_PAGE_SIZE);

  const visibleUsers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return users
      .filter((user) => {
        if (!normalizedSearch) return true;

        return (user.fullname || '').toLowerCase().includes(normalizedSearch)
          || (user.email || '').toLowerCase().includes(normalizedSearch);
      })
      .sort((firstUser, secondUser) => (
        getRegistrationTime(secondUser) - getRegistrationTime(firstUser)
      ));
  }, [searchTerm, users]);
  const renderedUsers = useMemo(
    () => visibleUsers.slice(0, visibleCount),
    [visibleCount, visibleUsers]
  );

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setMessage(null);

    try {
      const [usersSnapshot, readyProfilesSnapshot] = await Promise.all([
        firebase.getAllUsers(),
        firebase.getAllReadyProfiles()
      ]);
      const loadedUsers = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        plan: getUserPlan(doc.data().plan)
      }));
      const aliases = {};

      readyProfilesSnapshot.docs.forEach((doc) => {
        const userId = doc.data().user;
        if (!userId) return;
        aliases[userId] = [...(aliases[userId] || []), doc.id];
      });

      setUsers(loadedUsers);
      setProfileAliases(aliases);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to load users.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const updateUser = useCallback(async (userId, updates) => {
    setSavingId(userId);
    setMessage(null);

    try {
      await firebase.updateUserByAdmin(userId, updates);
      setUsers((currentUsers) => currentUsers.map((user) => (
        user.id === userId ? { ...user, ...updates } : user
      )));
      setMessage({ type: 'success', text: 'User updated successfully.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to update user.' });
    } finally {
      setSavingId('');
    }
  }, []);

  const activateUser = useCallback(async (userId, requestedProfileId) => {
    const profileId = requestedProfileId.trim();

    if (!profileId) {
      setMessage({ type: 'error', text: 'Enter a profile link ID before activating.' });
      return false;
    }

    setSavingId(userId);
    setMessage(null);

    try {
      await firebase.activateUserProfile(profileId, userId);
      setProfileAliases((aliases) => ({
        ...aliases,
        [userId]: Array.from(new Set([...(aliases[userId] || []), profileId]))
      }));
      setMessage({
        type: 'success',
        text: `Profile activated at /#/user/${profileId}.`
      });
      return true;
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Activation failed.' });
      return false;
    } finally {
      setSavingId('');
    }
  }, []);

  return (
    <Boundary>
      <div className="admin-users-page">
        <div className="admin-users-heading">
          <div>
            <h2>Registered Users</h2>
            <p className="text-subtle">
              Activate profiles, add aliases, and manage roles and plans.
            </p>
          </div>
          <button className="button button-small" onClick={loadUsers} type="button">
            Refresh
          </button>
        </div>

        {message ? (
          <p className={message.type === 'error' ? 'toast-error' : 'toast-success'}>
            {message.text}
          </p>
        ) : null}

        <div className="admin-users-search">
          <label htmlFor="admin-user-search">
            Search users
            <input
              className="input-form"
              id="admin-user-search"
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setVisibleCount(USERS_PAGE_SIZE);
              }}
              placeholder="Search by name or email"
              type="search"
              value={searchTerm}
            />
          </label>
          <span className="text-subtle">
            {visibleUsers.length}
            {' '}
            {visibleUsers.length === 1 ? 'user' : 'users'}
          </span>
        </div>

        {isLoading ? (
          <div className="loader"><LoadingOutlined /></div>
        ) : (
          <div className="admin-users-list">
            {renderedUsers.map((user) => (
              <UserCard
                aliases={profileAliases[user.id] || EMPTY_ALIASES}
                currentAdminId={currentAdminId}
                isSaving={savingId === user.id}
                key={user.id}
                onActivate={activateUser}
                onUpdate={updateUser}
                user={user}
              />
            ))}
            {!visibleUsers.length ? (
              <p className="text-subtle admin-users-empty">
                No users match this name or email.
              </p>
            ) : null}
            {visibleCount < visibleUsers.length ? (
              <div className="admin-users-load-more">
                <button
                  className="button button-small"
                  onClick={() => setVisibleCount((count) => count + USERS_PAGE_SIZE)}
                  type="button"
                >
                  Load more users
                </button>
                <span className="text-subtle">
                  Showing
                  {' '}
                  {renderedUsers.length}
                  {' '}
                  of
                  {' '}
                  {visibleUsers.length}
                </span>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </Boundary>
  );
};

export default Users;
